import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { History, Plus, X, Save, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

export const AboutHistoryCard = () => {
  const [about, setAbout] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reserve/about`).then(r => setAbout({ ...r.data, years: r.data.years.map(y => ({ ...y, speakersText: y.speakers.join(', ') })) }));
  }, []);

  const setYear = (i, k, v) => setAbout(a => ({ ...a, years: a.years.map((y, idx) => idx === i ? { ...y, [k]: v } : y) }));
  const addYear = () => setAbout(a => ({ ...a, years: [...a.years, { year: String(Number(a.years[a.years.length - 1]?.year || 2026) + 1), label: `${ordinal(a.years.length + 1)} Annual`, location: 'Herzl Camp · Webster, WI', speakersText: '', note: '' }] }));
  const removeYear = (i) => setAbout(a => ({ ...a, years: a.years.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const years = about.years.map(({ speakersText, ...y }) => ({ ...y, speakers: speakersText.split(',').map(s => s.trim()).filter(Boolean) }));
      await axios.put(`${BACKEND_URL}/api/reserve/admin/about`, { intro: about.intro, years }, { headers: getAuthHeaders() });
      toast.success('About / history saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  if (!about) return null;
  return (
    <Card data-testid="about-history-card">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><History className="w-4 h-4 text-wine" />About &amp; Speaker History <span className="text-xs text-gray-400 font-normal">shown on the home page</span></CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div><Label>Intro paragraph</Label><Textarea rows={3} value={about.intro} onChange={e => setAbout({ ...about, intro: e.target.value })} data-testid="about-intro-input" /></div>
        <div className="space-y-3">
          {about.years.map((y, i) => (
            <div key={i} className="rounded-lg border p-3 grid sm:grid-cols-[80px,1fr,1fr,auto] gap-3 items-start" data-testid={`about-year-${i}`}>
              <div><Label className="text-xs">Year</Label><Input value={y.year} onChange={e => setYear(i, 'year', e.target.value)} data-testid={`about-year-input-${i}`} /></div>
              <div className="space-y-2">
                <div><Label className="text-xs">Label</Label><Input value={y.label} onChange={e => setYear(i, 'label', e.target.value)} placeholder="First Annual" /></div>
                <div><Label className="text-xs">Location</Label><Input value={y.location} onChange={e => setYear(i, 'location', e.target.value)} /></div>
              </div>
              <div className="space-y-2">
                <div><Label className="text-xs">Speakers (comma-separated)</Label><Input value={y.speakersText} onChange={e => setYear(i, 'speakersText', e.target.value)} placeholder="Bob B., Steve L." data-testid={`about-speakers-input-${i}`} /></div>
                <div><Label className="text-xs">Note (optional)</Label><Input value={y.note || ''} onChange={e => setYear(i, 'note', e.target.value)} /></div>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 mt-5" onClick={() => removeYear(i)} data-testid={`about-remove-year-${i}`}><X className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addYear} data-testid="about-add-year-btn"><Plus className="w-3.5 h-3.5 mr-1.5" />Add a year</Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-gradient-to-r from-wine to-burgundy text-white ml-auto" data-testid="about-save-btn">{saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Save history</Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ordinal = (n) => ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'][n - 1] || `${n}th`;
